import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import ZonaSocialSelect from "jsx/components/CitoPlus/components/ZonaSocial/ZonaSocialSelect";
import axiosInstance from "../../../../../services/AxiosInstance";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import {
  FIELD_LAYOUTS,
  SelectVivienda,
} from "jsx/components/CitoPlus/components/FieldBasic";
import DatePickerRanger from "jsx/components/CitoPlus/components/DatePickerRanger";
import CheckboxOnOff from "jsx/components/CitoPlus/components/CheckboxOnOff";

const RESERVAS_URL = "/api/v3/zonas-sociales/reservas/";
const RESERVA_ESTADO_URL = "/api/v3/zonas-sociales/reservas/";
const ZONAS_URL = "/api/v3/zonas-sociales/zonas/dropdown/";

const STATUS_METADATA = Object.freeze({
  pendiente: { label: "Pendiente", variant: "warning" },
  confirmada: { label: "Confirmada", variant: "success" },
  cancelada: { label: "Cancelada", variant: "danger" },
});

const STATUS_OPTIONS = Object.keys(STATUS_METADATA);

const asDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (
  value,
  options = { dateStyle: "full", timeStyle: "short" },
) => {
  const date = asDate(value);
  if (!date) {
    return "-";
  }

  return new Intl.DateTimeFormat("es-CO", options).format(date);
};

const buildZones = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const buildEditState = (detail) => {
  if (!detail) {
    return null;
  }

  const personaUnidad =
    detail?.inmueble?.nombre ||
    detail?.inmueble?.interior_display ||
    detail?.unidad ||
    detail?.residente_vivienda ||
    "";

  return {
    titulo: detail.titulo || "",
    descripcion: detail.descripcion || "",
    all_day: Boolean(detail.all_day),
    start: asDate(detail.start ?? detail.fecha_inicio),
    end: asDate(detail.end ?? detail.fecha_fin),
    zona_uuid: detail.zona?.uuid || "",
    zona_nombre: detail.zona?.nombre || "",
    citofonia_slug: detail.inmueble?.slug || detail.citofonia_slug || "",
    unidad_label: personaUnidad,
  };
};

const payloadHasChanges = (payload) =>
  Boolean(
    payload && Object.keys(payload).some((key) => payload[key] !== undefined),
  );

const ReservaGestionModal = ({
  show,
  reservaUuid,
  onClose,
  onUpdated,
  cachedZones,
}) => {
  const [detail, setDetail] = useState(null);
  const [editState, setEditState] = useState(null);
  const [statusState, setStatusState] = useState({
    estado: "",
    descripcion: "",
  });

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [zones, setZones] = useState(() =>
    Array.isArray(cachedZones) ? cachedZones : [],
  );
  const [isLoadingZones, setIsLoadingZones] = useState(false);
  const [unidadSelectKey, setUnidadSelectKey] = useState(0);

  const resetState = useCallback(() => {
    setDetail(null);
    setEditState(null);
    setStatusState({ estado: "", descripcion: "" });
    setIsLoadingDetail(false);
    setIsSavingEdit(false);
    setIsUpdatingStatus(false);
    setIsEditing(false);
    setUnidadSelectKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!Array.isArray(cachedZones) || cachedZones.length === 0) {
      return;
    }

    setZones((prev) => {
      const sameLength = prev.length === cachedZones.length;
      const sameItems =
        sameLength &&
        prev.every((zone, index) => {
          const candidate = cachedZones[index];
          if (!zone || !candidate) {
            return zone === candidate;
          }
          return (
            zone.uuid === candidate.uuid && zone.nombre === candidate.nombre
          );
        });

      return sameItems ? prev : cachedZones;
    });
  }, [cachedZones]);

  useEffect(() => {
    if (!show) {
      resetState();
      return;
    }

    if (!reservaUuid) {
      notifyError("No se pudo determinar la reserva seleccionada.");
      return;
    }

    let isMounted = true;

    const fetchDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const response = await axiosInstance.get(
          `${RESERVAS_URL}${reservaUuid}/`,
        );
        if (!isMounted) {
          return;
        }
        const fetchedDetail = response.data;
        setDetail(fetchedDetail);
        setEditState(buildEditState(fetchedDetail));
        setStatusState({
          estado: fetchedDetail.estado || "pendiente",
          descripcion: fetchedDetail.descripcion || "",
        });
        setIsEditing(false);
        setUnidadSelectKey((prev) => prev + 1);
      } catch (_error) {
        if (isMounted) {
          notifyError("No fue posible cargar el detalle de la reserva.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingDetail(false);
        }
      }
    };

    const fetchZones = async () => {
      if (cachedZones && cachedZones.length > 0) {
        return;
      }
      setIsLoadingZones(true);
      try {
        const response = await axiosInstance.get(ZONAS_URL);
        if (!isMounted) {
          return;
        }
        setZones(buildZones(response?.data));
      } catch (_error) {
        if (isMounted) {
          notifyError("No fue posible obtener las zonas sociales disponibles.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingZones(false);
        }
      }
    };

    fetchZones();
    fetchDetail();

    return () => {
      isMounted = false;
    };
  }, [cachedZones, reservaUuid, resetState, show]);

  const zoneOptions = useMemo(
    () =>
      (zones || []).map((zone) => ({
        uuid: zone.uuid || zone.value || zone.id,
        nombre: zone.nombre || zone.label || zone.name,
      })),
    [zones],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((key) => ({
        value: key,
        label: STATUS_METADATA[key]?.label || key,
      })),
    [],
  );

  const handleDismiss = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const toggleEditing = useCallback(() => {
    if (!detail) {
      return;
    }
    if (isEditing) {
      setEditState(buildEditState(detail));
      setUnidadSelectKey((prev) => prev + 1);
    }
    setIsEditing((prev) => !prev);
  }, [detail, isEditing]);

  const handleEditChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setEditState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleRangeChange = useCallback((values) => {
    const [start, end] = values || [];
    setEditState((prev) => ({
      ...prev,
      start: start ? new Date(start) : null,
      end: end ? new Date(end) : null,
    }));
  }, []);

  const handleUnidadChange = useCallback((selection) => {
    const option = Array.isArray(selection) ? selection[0] : selection;
    if (!option) {
      setEditState((prev) => ({
        ...prev,
        citofonia_slug: "",
        unidad_label: "",
      }));
      setUnidadSelectKey((prev) => prev + 1);
      return;
    }

    const value =
      option.value === null || option.value === undefined
        ? ""
        : String(option.value).trim();
    const label =
      option.label === null || option.label === undefined
        ? value
        : String(option.label).trim();

    setEditState((prev) => ({
      ...prev,
      citofonia_slug: value,
      unidad_label: label,
    }));
  }, []);

  const handleZonaSelect = useCallback((value, option) => {
    setEditState((prev) => ({
      ...prev,
      zona_uuid: value || "",
      zona_nombre: option?.nombre || option?.label || option?.name || "",
    }));
  }, []);

  const handleStatusChange = useCallback((event) => {
    const { name, value } = event.target;
    setStatusState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateEditState = () => {
    if (!editState) {
      notifyError("No se encontró la información de la reserva a actualizar.");
      return false;
    }

    if (!editState.start || !editState.end) {
      notifyError("Debes seleccionar la fecha y la hora de inicio y fin.");
      return false;
    }

    const startTime = editState.start.getTime();
    const endTime = editState.end.getTime();
    if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
      notifyError("Las fechas seleccionadas no son válidas.");
      return false;
    }

    if (startTime >= endTime) {
      notifyError("La fecha de fin debe ser posterior a la fecha de inicio.");
      return false;
    }

    if (!editState.zona_uuid) {
      notifyError("Debes seleccionar la zona social asociada a la reserva.");
      return false;
    }

    return true;
  };

  const buildPatchPayload = () => {
    if (!detail || !editState) {
      return null;
    }

    const payload = {};

    if ((editState.titulo || "") !== (detail.titulo || "")) {
      payload.titulo = editState.titulo || "";
    }

    if ((editState.descripcion || "") !== (detail.descripcion || "")) {
      payload.descripcion = editState.descripcion || "";
    }

    if (Boolean(editState.all_day) !== Boolean(detail.all_day)) {
      payload.all_day = Boolean(editState.all_day);
    }

    const originalZone = detail.zona?.uuid || "";
    if ((editState.zona_uuid || "") !== originalZone) {
      payload.zona_uuid = editState.zona_uuid;
    }

    const originalStart = detail.start ?? detail.fecha_inicio;
    const originalEnd = detail.end ?? detail.fecha_fin;
    const editedStart = editState.start
      ? new Date(editState.start).toISOString()
      : null;
    const editedEnd = editState.end
      ? new Date(editState.end).toISOString()
      : null;

    if (editedStart && editedStart !== originalStart) {
      payload.fecha_inicio = editedStart;
    }
    if (editedEnd && editedEnd !== originalEnd) {
      payload.fecha_fin = editedEnd;
    }

    const originalSlug = detail.inmueble?.slug || detail.citofonia_slug || "";
    if ((editState.citofonia_slug || "") !== originalSlug) {
      payload.citofonia_slug = editState.citofonia_slug || null;
    }

    return payloadHasChanges(payload) ? payload : null;
  };

  const handleSaveChanges = async () => {
    if (!reservaUuid) {
      notifyError("No se identificó la reserva a actualizar.");
      return;
    }

    if (!validateEditState()) {
      return;
    }

    const payload = buildPatchPayload();

    if (!payloadHasChanges(payload)) {
      notifyError("No detectamos cambios para guardar.");
      return;
    }

    setIsSavingEdit(true);
    try {
      const response = await axiosInstance.patch(
        `${RESERVAS_URL}${reservaUuid}/`,
        payload,
      );
      const updated = response.data;
      setDetail(updated);
      setEditState(buildEditState(updated));
      setStatusState({
        estado: updated.estado || "pendiente",
        descripcion: updated.descripcion || "",
      });
      setUnidadSelectKey((prev) => prev + 1);
      setIsEditing(false);
      notifySuccess("La reserva se actualizó correctamente.");
      onUpdated?.();
    } catch (_error) {
      notifyError("No fue posible actualizar la reserva.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!detail || !reservaUuid) {
      notifyError("No se identificó la reserva a actualizar.");
      return;
    }

    const nextEstado = statusState.estado || "";
    const descripcion = statusState.descripcion || "";
    const originalEstado = detail.estado || "";
    const originalDescripcion = detail.descripcion || "";

    if (!nextEstado || !STATUS_METADATA[nextEstado]) {
      notifyError("Selecciona un estado válido para la reserva.");
      return;
    }

    if (
      nextEstado === originalEstado &&
      descripcion === (originalDescripcion || "")
    ) {
      notifyError("No detectamos cambios para guardar.");
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const response = await axiosInstance.patch(
        `${RESERVA_ESTADO_URL}${reservaUuid}/estado/`,
        {
          estado: nextEstado,
          descripcion: descripcion || undefined,
        },
      );
      const updated = response.data;
      setDetail(updated);
      setEditState(buildEditState(updated));
      setStatusState({
        estado: updated.estado || "pendiente",
        descripcion: updated.descripcion || "",
      });
      setUnidadSelectKey((prev) => prev + 1);
      notifySuccess("El estado de la reserva se actualizó correctamente.");
      onUpdated?.();
    } catch (_error) {
      notifyError("No fue posible actualizar el estado de la reserva.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderStatusBadge = (estado) => {
    const metadata = STATUS_METADATA[estado];
    if (!metadata) {
      return <span className="badge bg-secondary">Estado desconocido</span>;
    }
    return (
      <span className={`badge bg-${metadata.variant}`}>{metadata.label}</span>
    );
  };

  return (
    <Modal show={show} onHide={handleDismiss} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Gestión de reserva</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingDetail ? (
          <div className="text-center py-4">
            <SpinnerCitoPlus text="Cargando detalle de la reserva..." />
          </div>
        ) : detail ? (
          <div className="vstack gap-4">
            <section className="border rounded-3 p-3">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h5 className="mb-1">
                    {detail.titulo || "Reserva sin título"}
                  </h5>
                  <div className="small text-body-secondary">
                    Gestión y seguimiento de la reserva
                  </div>
                </div>
                {renderStatusBadge(detail.estado)}
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">Fecha</div>
                  <div className="fw-semibold">
                    {formatDateTime(detail.start || detail.fecha_inicio, {
                      dateStyle: "full",
                    })}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">Hora</div>
                  <div className="fw-semibold">
                    {`${formatDateTime(detail.start || detail.fecha_inicio, {
                      timeStyle: "short",
                    })} — ${formatDateTime(detail.end || detail.fecha_fin, {
                      timeStyle: "short",
                    })}`}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">
                    Zona social
                  </div>
                  {isEditing ? (
                    <ZonaSocialSelect
                      value={editState?.zona_uuid || ""}
                      onChange={handleZonaSelect}
                      options={zoneOptions}
                      autoFetch={false}
                      allowEmptyOption
                      placeholder="Selecciona una zona"
                      loading={isLoadingZones}
                      selectedLabel={editState?.zona_nombre || ""}
                    />
                  ) : (
                    <div className="fw-semibold">
                      {detail.zona?.nombre || "No disponible"}
                    </div>
                  )}
                  {isEditing && isLoadingZones && (
                    <div className="form-text">
                      Cargando zonas disponibles...
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">Unidad</div>
                  {isEditing ? (
                    <SelectVivienda
                      key={`unidad-selector-${unidadSelectKey}`}
                      layout={FIELD_LAYOUTS.VERTICAL}
                      showLabel
                      label="Unidad"
                      required={false}
                      defaultsValues={editState?.citofonia_slug || ""}
                      onChange={handleUnidadChange}
                      fieldClassName="mb-0"
                      contentClassName="w-100"
                      selectClassName="w-100"
                      isClearable
                    />
                  ) : (
                    <div className="fw-semibold">
                      {detail.inmueble?.nombre ||
                        detail.unidad ||
                        detail.residente_vivienda ||
                        "No disponible"}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">
                    Residente
                  </div>
                  <div className="fw-semibold">
                    {detail.residente?.nombre ||
                      detail.residente?.username ||
                      detail.residente_username ||
                      "No disponible"}
                  </div>
                  {detail.residente?.correo && (
                    <div className="small text-body-secondary mt-1">
                      {detail.residente.correo}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="small text-body-secondary mb-1">Título</div>
                  {isEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      name="titulo"
                      value={editState?.titulo || ""}
                      onChange={handleEditChange}
                    />
                  ) : (
                    <div className="fw-semibold">
                      {detail.titulo || "Reserva sin título"}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="border rounded-3 p-3">
              <h6 className="mb-3">Descripción</h6>
              {isEditing ? (
                <textarea
                  className="form-control"
                  name="descripcion"
                  rows={3}
                  value={editState?.descripcion || ""}
                  onChange={handleEditChange}
                />
              ) : (
                <div className="rounded-2 border p-3 bg-body-tertiary">
                  {detail.descripcion || "Sin descripción"}
                </div>
              )}
            </section>

            <section className="border rounded-3 p-3">
              <h6 className="mb-3">Horario</h6>
              {isEditing ? (
                <div className="vstack gap-3">
                  <CheckboxOnOff
                    id="reserva-all-day-switch"
                    name="all_day"
                    label="Reserva todo el día"
                    isChecked={Boolean(editState?.all_day)}
                    onChange={handleEditChange}
                  />
                  <DatePickerRanger
                    key={`daterange-${isEditing}`}
                    startDate={editState?.start || null}
                    endDate={editState?.end || null}
                    onChange={handleRangeChange}
                    selectsRange
                    withPortal
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    disabled={false}
                  />
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="small text-body-secondary mb-1">Inicio</div>
                    <div className="fw-semibold">
                      {formatDateTime(detail.start || detail.fecha_inicio)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-body-secondary mb-1">Fin</div>
                    <div className="fw-semibold">
                      {formatDateTime(detail.end || detail.fecha_fin)}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="border rounded-3 p-3">
              <h6 className="mb-3">Cambiar estado</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label" htmlFor="reserva-estado-select">
                    Estado
                  </label>
                  <select
                    id="reserva-estado-select"
                    name="estado"
                    className="form-select"
                    value={statusState.estado || ""}
                    onChange={handleStatusChange}
                    disabled={isUpdatingStatus}
                  >
                    <option value="">Selecciona un estado</option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-8">
                  <label
                    className="form-label"
                    htmlFor="reserva-estado-descripcion"
                  >
                    Comentarios
                  </label>
                  <input
                    id="reserva-estado-descripcion"
                    name="descripcion"
                    type="text"
                    className="form-control"
                    value={statusState.descripcion || ""}
                    onChange={handleStatusChange}
                    placeholder="Opcional"
                    disabled={isUpdatingStatus}
                  />
                </div>
              </div>
              <div className="text-end mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleStatusUpdate}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? (
                    <span className="d-inline-flex align-items-center gap-2">
                      <i
                        className="fas fa-circle-notch fa-spin"
                        aria-hidden="true"
                      />
                      <span>Actualizando...</span>
                    </span>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2" />
                      Actualizar estado
                    </>
                  )}
                </button>
              </div>
            </section>
          </div>
        ) : (
          <div className="alert alert-warning mb-0" role="alert">
            No se encontró información de la reserva seleccionada.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleDismiss}
        >
          Cerrar
        </button>
        {detail && (
          <>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={toggleEditing}
                  disabled={isSavingEdit}
                >
                  Cancelar edición
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveChanges}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? (
                    <span className="d-inline-flex align-items-center gap-2">
                      <i
                        className="fas fa-circle-notch fa-spin"
                        aria-hidden="true"
                      />
                      <span>Guardando...</span>
                    </span>
                  ) : (
                    <>
                      <i className="fas fa-save me-2" />
                      Guardar cambios
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={toggleEditing}
              >
                <i className="fas fa-edit me-2" />
                Editar reserva
              </button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReservaGestionModal;
